import { makeAutoObservable, runInAction } from 'mobx';
import type {
    ReferralInvitationDto,
    ReferralInvitationsDto,
    ReferralLedgerDto,
    ReferralLedgerEntryDto,
    ReferralOverviewDto,
} from '../../defs/api';
import type { RootStore } from './root-store';

const DEFAULT_PAGE_SIZE = Number(process.env.NEXT_PUBLIC_PAGE_LIMIT);

export class ReferralStore {
    rootStore: RootStore;

    loading = false;

    error = false;

    overview: ReferralOverviewDto | null = null;

    invitations: ReferralInvitationDto[] = [];

    invitationsTotal = 0;

    invitationsPage = 1;

    invitationsLimit = DEFAULT_PAGE_SIZE;

    ledger: ReferralLedgerEntryDto[] = [];

    ledgerTotal = 0;

    ledgerPage = 1;

    ledgerLimit = DEFAULT_PAGE_SIZE;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    async fetch(): Promise<void> {
        this.loading = true;
        this.error = false;

        try {
            const [overview, invitations, ledger]: [ReferralOverviewDto, ReferralInvitationsDto, ReferralLedgerDto] =
                await Promise.all([
                    this.rootStore.apiClient.referralController.referralControllerGetOverview(),
                    this.rootStore.apiClient.referralController.referralControllerGetInvitations({
                        limit: this.invitationsLimit,
                        offset: (this.invitationsPage - 1) * this.invitationsLimit,
                    }),
                    this.rootStore.apiClient.referralController.referralControllerGetLedger({
                        limit: this.ledgerLimit,
                        offset: (this.ledgerPage - 1) * this.ledgerLimit,
                    }),
                ]);

            runInAction(() => {
                this.overview = overview;
                this.invitations = invitations.items;
                this.invitationsTotal = invitations.total;
                this.ledger = ledger.items;
                this.ledgerTotal = ledger.total;
            });
        } catch (error) {
            runInAction(() => {
                this.error = true;
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    async setInvitationsPage(page: number): Promise<void> {
        this.invitationsPage = page;
        await this.fetchInvitations();
    }

    async setInvitationsLimit(limit: number | string | null): Promise<void> {
        const parsedLimit = Number(limit);
        if (!Number.isInteger(parsedLimit) || parsedLimit <= 0) {
            return;
        }
        this.invitationsLimit = parsedLimit;
        this.invitationsPage = 1;
        await this.fetchInvitations();
    }

    async setLedgerPage(page: number): Promise<void> {
        this.ledgerPage = page;
        await this.fetchLedger();
    }

    async setLedgerLimit(limit: number | string | null): Promise<void> {
        const parsedLimit = Number(limit);
        if (!Number.isInteger(parsedLimit) || parsedLimit <= 0) {
            return;
        }
        this.ledgerLimit = parsedLimit;
        this.ledgerPage = 1;
        await this.fetchLedger();
    }

    get invitationsPagesCount(): number {
        return Math.ceil(this.invitationsTotal / this.invitationsLimit) || 0;
    }

    get ledgerPagesCount(): number {
        return Math.ceil(this.ledgerTotal / this.ledgerLimit) || 0;
    }

    private async fetchInvitations(): Promise<void> {
        this.loading = true;
        this.error = false;

        try {
            const invitations = await this.rootStore.apiClient.referralController.referralControllerGetInvitations({
                limit: this.invitationsLimit,
                offset: (this.invitationsPage - 1) * this.invitationsLimit,
            });
            runInAction(() => {
                this.invitations = invitations.items;
                this.invitationsTotal = invitations.total;
            });
        } catch (error) {
            runInAction(() => {
                this.error = true;
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    private async fetchLedger(): Promise<void> {
        this.loading = true;
        this.error = false;

        try {
            const ledger = await this.rootStore.apiClient.referralController.referralControllerGetLedger({
                limit: this.ledgerLimit,
                offset: (this.ledgerPage - 1) * this.ledgerLimit,
            });
            runInAction(() => {
                this.ledger = ledger.items;
                this.ledgerTotal = ledger.total;
            });
        } catch (error) {
            runInAction(() => {
                this.error = true;
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }
}
